import { useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AiSettingsIcon from '@/app/common/components/AiSettingsIcon';
import SubtleBadge from '@/components/common/SubtleBadge';
import type {
  MassiveHistoryItem,
  MassiveKeywordFilter
} from '@/app/KeywordResearch/types/massiveApiTypes';

const MODE_LABELS = {
  include: 'شامل',
  exclude: 'حذف'
} as const;

const LOGIC_LABELS = {
  and: 'و',
  or: 'یا'
} as const;

type FilterPopoverContentProps = {
  filter: MassiveKeywordFilter;
};

const FilterPopoverContent = ({ filter }: FilterPopoverContentProps) => (
  <div className="fs--1 text-800">
    <div className="mb-2 d-flex align-items-center gap-1">
      <span className="text-600">نوع:</span>
      <SubtleBadge
        bg={filter.mode === 'include' ? 'success' : 'danger'}
        className="fs--2"
      >
        {MODE_LABELS[filter.mode]}
      </SubtleBadge>
    </div>
    <div className="mb-2">
      <span className="text-600">منطق:</span>{' '}
      <span className="fw-semi-bold">{LOGIC_LABELS[filter.logic]}</span>
    </div>
    <div className="d-flex align-items-center flex-wrap gap-1">
      <span className="text-600">عبارت‌ها:</span>
      {filter.filter_terms.map(term => (
        <SubtleBadge key={term} bg="info" className="fs--2">
          {term}
        </SubtleBadge>
      ))}
    </div>
  </div>
);

type MassiveFilterRendererProps = {
  data?: MassiveHistoryItem;
};

const MassiveFilterRenderer = ({ data }: MassiveFilterRendererProps) => {
  const [show, setShow] = useState(false);

  if (!data) return null;

  const { ai_smart_filter, keyword_filter, task_id } = data;
  const hasPersonalFilter = !ai_smart_filter && keyword_filter;
  const hasAiFilter = Boolean(ai_smart_filter);

  if (!hasAiFilter && !hasPersonalFilter) return null;

  const icon = hasAiFilter ? (
    <span className="d-inline-flex text-success" title="فیلتر AI">
      <AiSettingsIcon size={16} />
    </span>
  ) : (
    <span
      className="d-inline-flex"
      style={{ color: '#a855f7' }}
      title="فیلتر شخصی"
    >
      <FontAwesomeIcon icon="filter" className="fs--1" />
    </span>
  );

  if (!keyword_filter) {
    return (
      <div className="d-flex align-items-center h-100">{icon}</div>
    );
  }

  return (
    <div className="d-flex align-items-center h-100 gap-3">
      {icon}
      <OverlayTrigger
        show={show}
        onToggle={setShow}
        trigger="click"
        rootClose
        placement="left"
        popperConfig={{
          modifiers: [
            {
              name: 'zIndex',
              enabled: true,
              phase: 'write',
              fn({ state }) {
                state.styles.popper.zIndex = '10050';
              }
            }
          ]
        }}
        overlay={
          <Popover
            id={`massive-filter-${task_id}`}
            className="shadow-sm massive-filter-popover"
            style={{ minWidth: 220, maxWidth: 280 }}
          >
            <Popover.Header as="h6" className="fs--1 py-2 px-3 mb-0">
              {hasAiFilter ? 'فیلتر AI' : 'فیلتر شخصی'}
            </Popover.Header>
            <Popover.Body className="p-3">
              <FilterPopoverContent filter={keyword_filter} />
            </Popover.Body>
          </Popover>
        }
      >
        <button
          type="button"
          className="btn btn-link btn-sm p-0 text-600 lh-1"
          onClick={event => event.stopPropagation()}
          aria-label="نمایش جزئیات فیلتر"
        >
          <FontAwesomeIcon icon="info-circle" className="fs--1" />
        </button>
      </OverlayTrigger>
    </div>
  );
};

export default MassiveFilterRenderer;
